import { z } from 'zod';

/**
 * CallSense - AI call analytics.
 *
 * The apps/intelligence Python pipeline fetches CallerDesk recordings,
 * analyzes them with Gemini (transcript, summary, travel requirements,
 * 11-dimension agent scoring) and upserts one row per call into
 * `call_insights`. This API surface is read-only aggregation over that
 * table for the admin CallSense dashboard (`ops.callsense.read`).
 */

const IsoDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const CALL_OUTCOMES = [
  'booked',
  'quote_sent',
  'follow_up_needed',
  'not_interested',
  'info_only',
  'complaint',
  'other',
] as const;
export const CallOutcomeSchema = z.enum(CALL_OUTCOMES);
export type CallOutcome = z.infer<typeof CallOutcomeSchema>;

export const CALL_SENTIMENTS = ['positive', 'neutral', 'negative'] as const;
export const CallSentimentSchema = z.enum(CALL_SENTIMENTS);
export type CallSentiment = z.infer<typeof CallSentimentSchema>;

/** The 11 scored dimensions (1-5 each, null = didn't apply on that call). */
export const CALLSENSE_SCORE_FIELDS = [
  'politeness_score',
  'greeting_score',
  'empathy_score',
  'active_listening_score',
  'product_knowledge_score',
  'needs_identification_score',
  'objection_handling_score',
  'upsell_cross_sell_score',
  'call_control_score',
  'closing_score',
  'language_clarity_score',
] as const;
export type CallsenseScoreField = (typeof CALLSENSE_SCORE_FIELDS)[number];

const DimensionAveragesSchema = z.record(z.string(), z.number().nullable());

export const CallsenseRangeQuerySchema = z.object({
  /** Inclusive IST calendar date (YYYY-MM-DD). Defaults to 30 days back. */
  from: IsoDateOnly.optional(),
  /** Inclusive IST calendar date (YYYY-MM-DD). Defaults to today. */
  to: IsoDateOnly.optional(),
  /** Filter to a single agent (exact `agentName` match). */
  agent: z.string().trim().min(1).max(200).optional(),
  /** Channel filter: all (default) / cd (CallerDesk) / wa (WhatsApp). */
  channel: z.enum(['all', 'cd', 'wa']).optional(),
});
export type CallsenseRangeQuery = z.infer<typeof CallsenseRangeQuerySchema>;

export const CallsenseCallsQuerySchema = CallsenseRangeQuerySchema.extend({
  outcome: CallOutcomeSchema.optional(),
  sentiment: CallSentimentSchema.optional(),
  /** inbound / outbound / unknown. */
  direction: z.enum(['inbound', 'outbound', 'unknown']).optional(),
  /** Exact `primaryLanguage` match (e.g. "Hinglish"). */
  language: z.string().trim().min(1).max(100).optional(),
  /** Free-text search over reason, summary, destination and agent name. */
  q: z.string().trim().min(1).max(200).optional(),
  /** Only calls whose overall score is at or above this. */
  minScore: z.coerce.number().min(0).max(100).optional(),
  /** Only calls whose overall score is at or below this (find weak calls). */
  maxScore: z.coerce.number().min(0).max(100).optional(),
  /** Only escalation-recommended / red-flagged calls. */
  flaggedOnly: z.coerce.boolean().optional(),
  /** Only calls needing a follow-up. */
  followUpOnly: z.coerce.boolean().optional(),
  /** Only scored conversations (drops voicemails / no-answer calls). */
  scoredOnly: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type CallsenseCallsQuery = z.infer<typeof CallsenseCallsQuerySchema>;

export const CallsenseAgentScorecardSchema = z.object({
  agentName: z.string(),
  numCalls: z.number().int(),
  totalMinutes: z.number(),
  avgOverallScore: z.number().nullable(),
  bookingRate: z.number(),
  escalationRate: z.number(),
  followUpRate: z.number(),
  negativeSentimentRate: z.number(),
  /** Per-dimension averages keyed by score field name. */
  dimensionAverages: DimensionAveragesSchema,
});
export type CallsenseAgentScorecard = z.infer<typeof CallsenseAgentScorecardSchema>;

export const CallsenseOverviewResponseSchema = z.object({
  totals: z.object({
    calls: z.number().int(),
    minutes: z.number(),
    avgOverallScore: z.number().nullable(),
    bookings: z.number().int(),
    bookingRate: z.number(),
    escalations: z.number().int(),
    redFlagCalls: z.number().int(),
    followUpsRequired: z.number().int(),
  }),
  outcomes: z.record(z.string(), z.number().int()),
  sentiments: z.record(z.string(), z.number().int()),
  /** Calls per detected primary language (feeds the language filter). */
  languages: z.record(z.string(), z.number().int()),
  dimensionAverages: DimensionAveragesSchema,
  agents: z.array(CallsenseAgentScorecardSchema),
  /** Daily trend across the range (IST calendar days with >= 1 call). */
  trend: z.array(
    z.object({
      date: IsoDateOnly,
      calls: z.number().int(),
      avgOverallScore: z.number().nullable(),
    }),
  ),
  /** Trailing 14 IST days (gap-filled) that feed the KPI sparklines, regardless
   *  of the selected range (so "Today" still shows a trend shape). */
  sparkTrend: z.array(
    z.object({
      date: IsoDateOnly,
      calls: z.number().int(),
      avgOverallScore: z.number().nullable(),
    }),
  ),
  /** % change of each headline metric vs the comparison period (same weekday
   *  last week for a single day, else the preceding equal-length window). */
  deltas: z.object({
    calls: z.number(),
    minutes: z.number(),
    avgScore: z.number(),
    bookings: z.number(),
    negative: z.number(),
    escalations: z.number(),
  }),
});
export type CallsenseOverviewResponse = z.infer<typeof CallsenseOverviewResponseSchema>;

export const CallsenseCallRowSchema = z.object({
  id: z.string(),
  callId: z.string(),
  callSid: z.string().nullable(),
  agentName: z.string().nullable(),
  callDate: z.string().nullable(),
  direction: z.string(),
  /** CALLERDESK (phone) or WHATSAPP (WhatsApp voice call). */
  channel: z.string(),
  durationMinutes: z.number().nullable(),
  primaryLanguage: z.string().nullable(),
  callReason: z.string().nullable(),
  callOutcome: z.string(),
  sentimentCustomer: z.string(),
  destination: z.string().nullable(),
  overallScore: z.number().nullable(),
  /** Agent's share of talk time (%) for this call, from the diarized
   *  transcript. Null when not computed. */
  agentTalkPct: z.number().nullable(),
  redFlagCount: z.number().int(),
  escalationRecommended: z.boolean(),
  followUpRequired: z.boolean(),
  recordingUrl: z.string().nullable(),
});
export type CallsenseCallRow = z.infer<typeof CallsenseCallRowSchema>;

export const CallsenseCallsResponseSchema = z.object({
  items: z.array(CallsenseCallRowSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type CallsenseCallsResponse = z.infer<typeof CallsenseCallsResponseSchema>;

export const CallsenseCallDetailSchema = CallsenseCallRowSchema.extend({
  summary: z.string().nullable(),
  /** Full analysis record produced by the pipeline (spec §6 shape). */
  analysis: z.record(z.string(), z.unknown()),
});
export type CallsenseCallDetail = z.infer<typeof CallsenseCallDetailSchema>;

// ---------------------------------------------------------------------------
// Calling report (daily, user-wise: Total -> TL -> agents)
// ---------------------------------------------------------------------------

/** Outbound / inbound call breakdown. CallerDesk only contributes `accepted`
 *  today (connected calls); WhatsApp gives the full split. `missed`/`declined`
 *  for CallerDesk arrive with the premium webhook. */
export const CallingReportBreakdownSchema = z.object({
  total: z.number().int(),
  accepted: z.number().int(),
  missed: z.number().int(),
  declined: z.number().int(),
});
export type CallingReportBreakdown = z.infer<typeof CallingReportBreakdownSchema>;

export const CallingReportRowSchema = z.object({
  /** total = team total; tl = team-lead group; agent = agent under a TL. */
  kind: z.enum(['total', 'tl', 'agent']),
  id: z.string(),
  name: z.string(),
  /** The TL id an agent sits under (null for total / tl rows). */
  parentId: z.string().nullable(),
  /** Agents under a TL row (null for total / agent rows). */
  agentCount: z.number().int().nullable(),
  tasks: z.object({
    todayFollowUps: z.number().int(),
    freshLeads: z.number().int(),
    missed: z.number().int(),
    total: z.number().int(),
    achieved: z.number().int(),
  }),
  /** Talk minutes by channel. */
  callerdeskMinutes: z.number(),
  whatsappMinutes: z.number(),
  /** Avg CallSense quality score (0-100) over the agent's analyzed calls. */
  avgScore: z.number().nullable(),
  /** Agent's share of talk time (%), from the diarized transcript. Ideal
   *  40-60%. Null when no talk-time is computed yet. */
  agentTalkPct: z.number().nullable(),
  outbound: CallingReportBreakdownSchema,
  inbound: CallingReportBreakdownSchema,
});
export type CallingReportRow = z.infer<typeof CallingReportRowSchema>;

export const CallingReportResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  rows: z.array(CallingReportRowSchema),
});
export type CallingReportResponse = z.infer<typeof CallingReportResponseSchema>;
